/**
 * SOLID Review
 * S — Strong. One concern: React's two ways into the client core — read the table, call a use case.
 * O — Good. A new use case needs nothing here; it is one more key of the port.
 * L — N/A — hooks.
 * I — Good. Two hooks, each one thing: a selection, one bound op.
 * D — Strong. The provider's resolved ports and the Application's port types; no store, no adapter.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useComponentFramework } from '../uml-components/ComponentFramework';
import { clientContext } from './ClientRequests';
import type { TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IContextRequest } from '../../application/ports/IContextRequest';

/**
 * Read the table through `ClientStateView` (frontend strict-ports plan, FE1e, 2026-10-03).
 *
 * The SAME CONTRACT AS ZUSTAND 5's `useStore`: the selector must return a value that is stable while
 * the store is unchanged — a field, or an object the store holds, never a new object built per call —
 * or React's `useSyncExternalStore` re-renders forever. Every `useGameStore((s) => …)` call site this
 * replaces already followed it.
 */
export function useClientView<T>(selector: (state: TClientViewState) => T): T {
  const view = useComponentFramework().clientStateView;
  return useSyncExternalStore(view.subscribe, () => selector(view.getSnapshot()));
}

/** The Application's lollipop. Components take one op at a time, through `useClientAction`. */
function useClientUseCases(): IPortClientUseCases {
  return useComponentFramework().clientUseCases;
}

type TUseCase = keyof IPortClientUseCases;

/**
 * One use case, bound: call it with its command and it mints the context (`clientContext`). Stable
 * across renders while the port is, so it may sit in an effect's dependencies.
 *
 * `budgetMs` shortens the deadline for a caller that stops waiting sooner — the name modal gives up
 * after 10 s, and an answer after that is then refused at the port instead of applied behind its back.
 */
export function useClientAction<K extends TUseCase>(op: K, budgetMs?: number): (command: TClientUseCaseInput<K>) => ReturnType<IPortClientUseCases[K]> {
  const useCases = useClientUseCases();
  return useCallback((command: TClientUseCaseInput<K>) => {
    // The one cast: an op picked by key cannot be called with its own argument types without it.
    const call = useCases[op] as unknown as (ctx: IContextRequest, command: TClientUseCaseInput<K>) => ReturnType<IPortClientUseCases[K]>;
    return call(clientContext(Date.now(), budgetMs), command);
  }, [useCases, op, budgetMs]);
}
